import moment from 'moment-timezone';

import i18n from '@/i18n';

import type { MissionPeriod as CompanyNeedMissionPeriod } from '@/modules/companyNeed/views/details/missions/types';
import type { MissionPeriod } from '@/types/mission';

export const CONVENTIONAL_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Converts a calendar-day string to ISO 8601 UTC midnight (e.g. "2024-01-15T00:00:00.000Z").
 * Accepts any moment-parseable format via the optional `format` parameter;
 * defaults to YYYY-MM-DD (HTML date input value).
 */
export const toCalendarDayUtcIso = (
	value: string | undefined | null,
	format?: string
): string | undefined => {
	if (!value) return undefined;
	const parsed = moment.utc(value, format ?? CONVENTIONAL_DATE_FORMAT);
	return parsed.isValid() ? parsed.startOf('day').toISOString() : undefined;
};

/**
 * Converts a calendar-day string to ISO 8601 UTC end-of-day (e.g. "2024-01-15T23:59:59.999Z").
 * Accepts any moment-parseable format via the optional `format` parameter;
 * defaults to YYYY-MM-DD (HTML date input value).
 */
export const toCalendarDayEndUtcIso = (
	value: string | undefined | null,
	format?: string
): string | undefined => {
	if (!value) return undefined;
	const parsed = moment.utc(value, format ?? CONVENTIONAL_DATE_FORMAT);
	return parsed.isValid() ? parsed.endOf('day').toISOString() : undefined;
};

/**
 * Same as toCalendarDayUtcIso but returns a Date object (UTC midnight).
 * Useful when the target type expects Date rather than string.
 */
export const toCalendarDayUtcDate = (
	value: string | undefined | null,
	format?: string
): Date | undefined => {
	if (!value) return undefined;
	const parsed = moment.utc(value, format ?? CONVENTIONAL_DATE_FORMAT);
	return parsed.isValid() ? parsed.startOf('day').toDate() : undefined;
};

/**
 * Time unit thresholds in their respective units
 */
const TIME_THRESHOLDS = {
	MINUTE: 1,
	HOUR: 1,
	DAY_IN_HOURS: 24,
	WEEK_IN_DAYS: 7,
	MONTH_IN_WEEKS: 4,
	YEAR_IN_MONTHS: 12,
} as const;

/**
 * Display options for relative time formatting
 */
export interface RelativeTimeOptions {
	/**
	 * Format style
	 * - 'short': Compact format (e.g., "5min", "3h", "2j")
	 * - 'medium': Semi-detailed format (e.g., "il y a 5j", "il y a 2sem")
	 * - 'long': Detailed format (e.g., "Il y a 5 jours", "Il y a plus de 2 mois")
	 */
	style?: 'short' | 'medium' | 'long';

	/**
	 * Whether to include granular time units (minutes, hours)
	 * If false, minimum unit will be days
	 */
	includeGranular?: boolean;

	/**
	 * Fallback date format when timestamp is too old
	 * Default: "DD/MM/YYYY"
	 */
	fallbackFormat?: string;
}

/**
 * Formats a timestamp into a relative time string.
 */
export const formatRelativeTime = (
	timestamp: number | string | Date,
	options: RelativeTimeOptions = {}
): string => {
	const {
		style = 'short',
		includeGranular = true,
		fallbackFormat = 'DD/MM/YYYY',
	} = options;

	const now = moment();
	const date = moment(timestamp);

	// Guard against null/invalid input so we never render moment's "Invalid date".
	if (!date.isValid()) {
		return '';
	}

	const diffMinutes = now.diff(date, 'minutes');
	const diffHours = now.diff(date, 'hours');
	const diffDays = now.diff(date, 'days');
	const diffWeeks = now.diff(date, 'weeks');
	const diffMonths = now.diff(date, 'months');
	const diffYears = now.diff(date, 'years');

	if (includeGranular && diffMinutes < TIME_THRESHOLDS.MINUTE) {
		return i18n.global.t('global.relativeTime.justNow');
	}

	if (includeGranular && diffMinutes < TIME_THRESHOLDS.DAY_IN_HOURS * 60) {
		if (diffHours < TIME_THRESHOLDS.HOUR) {
			return i18n.global.t('global.relativeTime.minutesAgo', {
				count: diffMinutes,
			});
		}

		if (diffHours < TIME_THRESHOLDS.DAY_IN_HOURS) {
			return i18n.global.t('global.relativeTime.hoursAgo', {
				count: diffHours,
			});
		}
	}

	if (diffDays === 0) {
		if (style === 'short') {
			return i18n.global.t('global.todayShort');
		}

		return i18n.global.t('global.today');
	}

	if (diffDays === 1) {
		return i18n.global.t('global.yesterday');
	}

	if (diffDays < TIME_THRESHOLDS.WEEK_IN_DAYS) {
		if (style === 'short') {
			return i18n.global.t('global.relativeTime.daysShort', {
				count: diffDays,
			});
		}
		if (style === 'medium') {
			return i18n.global.t('global.relativeTime.daysAgo', { count: diffDays });
		}
		return i18n.global.t('global.relativeTime.moreThanDays', {
			count: diffDays,
		});
	}

	if (diffWeeks < TIME_THRESHOLDS.MONTH_IN_WEEKS) {
		if (style === 'short') {
			return i18n.global.t('global.relativeTime.weeksShort', {
				count: diffWeeks,
			});
		}
		if (style === 'medium') {
			return i18n.global.t('global.relativeTime.weeksAgo', {
				count: diffWeeks,
			});
		}
		const weekLabel =
			diffWeeks > 1
				? i18n.global.t('global.weeksLeft.plural')
				: i18n.global.t('global.weeksLeft.singular');
		return `${diffWeeks} ${weekLabel}`;
	}

	if (diffMonths < TIME_THRESHOLDS.YEAR_IN_MONTHS) {
		if (style === 'short') {
			return i18n.global.t('global.relativeTime.monthsShort', {
				count: diffMonths,
			});
		}
		if (style === 'medium') {
			return i18n.global.t('global.relativeTime.monthsAgo', {
				count: diffMonths,
			});
		}
		return i18n.global.t('global.relativeTime.moreThanMonths', {
			count: diffMonths,
		});
	}

	if (diffYears >= 1) {
		if (style === 'short' || style === 'medium') {
			return date.format(fallbackFormat);
		}
		return i18n.global.t('global.relativeTime.moreThanYears', {
			count: diffYears,
		});
	}

	return date.format(fallbackFormat);
};

export const sortDatesFromRecentToOldest = (dateA: number, dateB: number) => {
	const dateAFormatted = moment(dateA);
	const dateBFormatted = moment(dateB);

	if (dateAFormatted.isBefore(dateBFormatted)) {
		return 1;
	}

	if (dateAFormatted.isAfter(dateBFormatted)) {
		return -1;
	}

	return 0;
};

export const getStartMissionDate = (
	periods: MissionPeriod[] | CompanyNeedMissionPeriod[]
) => Math.min(...periods.map((period) => moment(period.start).valueOf()));

export const getEndMissionDate = (
	periods: MissionPeriod[] | CompanyNeedMissionPeriod[]
) => Math.max(...periods.map((period) => moment(period.end).valueOf()));

/**
 * Extracts the YYYY-MM-DD portion from an ISO or date string,
 * suitable for HTML date input values.
 */
export const toDateInputValue = (value: unknown): string => {
	if (typeof value !== 'string' || !value) return '';
	return value.length >= 10 ? value.slice(0, 10) : value;
};

/**
 * Ensures a date value is a full ISO 8601 UTC datetime string.
 * Pass-through for values already containing a time part;
 * appends T00:00:00.000Z for calendar-day strings (YYYY-MM-DD).
 */
export const toISODatetime = (val: unknown): string | undefined => {
	if (!val || typeof val !== 'string') return undefined;
	if (val.includes('T')) return val;
	return toCalendarDayUtcIso(val);
};

/**
 * Converts a French DD/MM/YYYY date string to YYYY-MM-DD.
 */
export const parseFrenchDateToISO = (value: string): string | null => {
	const iso = toCalendarDayUtcIso(value.trim(), 'DD/MM/YYYY');
	return iso ? iso.slice(0, 10) : null;
};

export const getFormattedHour = (hour: number, format = 'date.time2') => {
	if (!hour) return '';
	const h = hour.toString();
	if (h.length === 3) {
		return moment(h, 'Hmm').format(i18n.global.t(format));
	}
	return moment(h.padStart(4, '0'), 'HHmm').format(i18n.global.t(format));
};

export const formatDuration = (hour: number) => {
	const d = moment.duration(hour * 1000);
	const h = Math.floor(d.asHours());
	const m = Math.floor(d.asMinutes()) - h * 60;
	const minutes = m > 0 ? `${m.toString().padStart(2, '0')}m` : '';
	return `${h}h${minutes}`;
};

export const formatDateDayAndMonth = (timestamp: number): string => {
	return moment(timestamp).format('DD MMM');
};

export const formatDate = (
	timestamp: number,
	format = 'DD/MM/YYYY'
): string => {
	return moment(timestamp).format(format);
};

export const formatHour = (timestamp: number, format = 'HH:mm'): string => {
	return moment(timestamp).format(format);
};
